import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { STUDIO_COPY } from '../config';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { listAdminRttsSupportIssues } from '../support/rttsSupportApi';
import type { LscAdminSupportIssue } from '../../lsc/types';
import { supportIssueCategoryLabel } from '../../lsc/supportIssueCopy';

type InboxView = 'open' | 'archived' | 'deleted';

const StudioAdminSupportPage: React.FC = () => {
  useStudioDocumentTitle('Support inbox');
  const { user } = useUser();
  const [view, setView] = useState<InboxView>('open');
  const [issues, setIssues] = useState<LscAdminSupportIssue[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.rtts_admin !== true) return;
    let cancelled = false;
    void listAdminRttsSupportIssues(view)
      .then((rows) => {
        if (!cancelled) setIssues(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load inbox');
      });
    return () => {
      cancelled = true;
    };
  }, [user?.rtts_admin, view]);

  if (user?.rtts_admin !== true) {
    return <Navigate to="/studio" replace />;
  }

  return (
    <div className="studio-panel space-y-4" data-testid="studio-admin-support">
      <h1 className="text-2xl font-semibold">{STUDIO_COPY.adminSupportTitle}</h1>
      <div className="flex flex-wrap gap-2">
        {(['open', 'archived', 'deleted'] as InboxView[]).map((tab) => (
          <button
            key={tab}
            type="button"
            className={view === tab ? 'studio-btn-primary' : 'studio-btn-ghost'}
            onClick={() => setView(tab)}
            data-testid={`studio-admin-support-tab-${tab}`}
          >
            {tab === 'open'
              ? STUDIO_COPY.adminSupportOpen
              : tab === 'archived'
                ? STUDIO_COPY.adminSupportArchived
                : STUDIO_COPY.adminSupportDeleted}
          </button>
        ))}
      </div>
      {error ? (
        <p className="text-red-300" role="alert">
          {error}
        </p>
      ) : null}
      {issues.length === 0 ? (
        <p className="text-[var(--studio-muted)]">No reports in this view.</p>
      ) : (
        <ul className="space-y-3" data-testid="studio-admin-support-list">
          {issues.map((issue) => (
            <li key={issue.issue_id}>
              <Link
                to={`/studio/admin/support/${issue.issue_id}`}
                className="block rounded border border-white/10 p-3 no-underline"
              >
                <p className="font-medium text-white">
                  {issue.unread ? `${STUDIO_COPY.supportUnread} · ` : ''}
                  {issue.title}
                </p>
                <p className="text-xs text-[var(--studio-muted)]">
                  {supportIssueCategoryLabel(issue.category)} · {issue.reporter_uid} ·{' '}
                  {issue.context?.job_id || 'no job'}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StudioAdminSupportPage;
